import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

/* ── Styled components ───────────────────────────────── */
const SliderBox = styled.div`
  position: relative;
  width: 100%;
  height: 88vh;
  overflow: hidden;
  @media only screen and (max-width: 949px) {
    height: 60vw;
    min-height: 220px;
  }
`;

const Slide = styled.div`
  position: absolute;
  inset: 0;
  background-image: url(${(p) => p.img});
  background-size: cover;
  background-position: center;
  opacity: ${(p) => (p.active ? 1 : 0)};
  transition: opacity 0.9s ease;
  display: flex;
  align-items: center;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 60%, transparent 100%);
`;

const TextBox = styled.div`
  position: relative;
  z-index: 2;
  padding: 0 8rem;
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
  max-width: 55%;
  @media only screen and (max-width: 949px) {
    padding: 0 2rem;
    max-width: 80%;
    gap: 0.7rem;
  }
`;

const Title = styled.h2`
  color: white;
  font-size: 4rem;
  font-weight: 700;
  margin: 0;
  line-height: 1.1;
  text-transform: uppercase;
  letter-spacing: 0.08rem;
  @media only screen and (max-width: 949px) {
    font-size: 2.2rem;
  }
`;

const Subtitle = styled.p`
  color: rgba(255,255,255,0.88);
  font-size: 1.6rem;
  font-weight: 300;
  margin: 0;
  letter-spacing: 0.06rem;
  @media only screen and (max-width: 949px) {
    font-size: 1.2rem;
  }
`;

const CtaBtn = styled(Link)`
  display: inline-block;
  width: fit-content;
  background-color: white;
  color: black;
  border-radius: 0.7rem;
  padding: 0.9rem 2rem;
  font-weight: bold;
  letter-spacing: 0.2rem;
  font-size: 1.3rem;
  text-decoration: none;
  text-transform: uppercase;
  transition: all 0.3s;
  border: 2px solid white;
  &:hover {
    background-color: transparent;
    color: white;
    transform: translateY(-3px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  }
  @media only screen and (max-width: 949px) {
    font-size: 1.1rem;
    padding: 0.7rem 1.4rem;
    letter-spacing: 0.1rem;
  }
`;

const ArrowBtn = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${(p) => (p.dir === "left" ? "left: 1.5rem;" : "right: 1.5rem;")}
  z-index: 10;
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  border: none;
  background-color: rgba(255,255,255,0.25);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  backdrop-filter: blur(4px);
  transition: background 0.2s;
  svg { transform: scale(1.6); }
  &:hover { background-color: rgba(255,255,255,0.45); }
  @media only screen and (max-width: 949px) {
    width: 3.5rem;
    height: 3.5rem;
    svg { transform: scale(1.2); }
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.6rem;
  z-index: 10;
`;

const Dot = styled.button`
  width: ${(p) => (p.active ? "2rem" : "0.7rem")};
  height: 0.7rem;
  border-radius: 1rem;
  background: ${(p) => (p.active ? "white" : "rgba(255,255,255,0.5)")};
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  padding: 0;
`;

const shimmer = keyframes`
  100% { background-position: right -200px top 0; }
`;
const SkeletonBox = styled.div`
  width: 100%;
  height: 88vh;
  background: linear-gradient(90deg, #e0e0e0 0px, #f0f0f0 100px, #e0e0e0 200px) left -200px top 0 / 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
  @media only screen and (max-width: 949px) { height: 60vw; min-height: 220px; }
`;

/* ── Component ───────────────────────────────────────── */
const Slider = () => {
  const [slides, setSlides] = useState([]);
  const [current, setCurrent] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSlides = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/slider/published`);
        const data = await res.json();
        if (data.success && data.sliders.length > 0) {
          setSlides(data.sliders);
        }
      } catch (e) {
        console.error("Slider fetch error:", e);
      }
      setLoading(false);
    };
    fetchSlides();
  }, []);

  // Auto-advance every 5s
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides]);

  const prev = () => setCurrent((c) => (c - 1 + slides.length) % slides.length);
  const next = () => setCurrent((c) => (c + 1) % slides.length);

  if (loading) return <SkeletonBox />;
  if (slides.length === 0) return null;

  return (
    <SliderBox>
      {slides.map((slide, idx) => (
        <Slide key={slide._id} img={slide.image} active={idx === current}>
          <Overlay />
          <TextBox>
            <Title data-aos="fade-right">{slide.title}</Title>
            {slide.subtitle && (
              <Subtitle data-aos="fade-left">{slide.subtitle}</Subtitle>
            )}
            {slide.ctaText && slide.ctaLink && (
              <CtaBtn
                to={slide.ctaLink.startsWith("http") ? slide.ctaLink : slide.ctaLink}
                data-aos="fade-in"
              >
                {slide.ctaText}
              </CtaBtn>
            )}
          </TextBox>
        </Slide>
      ))}

      {slides.length > 1 && (
        <>
          <ArrowBtn dir="left" onClick={prev}><ChevronLeft /></ArrowBtn>
          <ArrowBtn dir="right" onClick={next}><ChevronRight /></ArrowBtn>
          <Dots>
            {slides.map((_, idx) => (
              <Dot key={idx} active={idx === current} onClick={() => setCurrent(idx)} />
            ))}
          </Dots>
        </>
      )}
    </SliderBox>
  );
};

export default Slider;
